// router/routes.tsx
import React from 'react';
// 2026-06-21: /login is an ORPHAN ROUTE — makes Login.tsx reachable but
// is NOT wired into the auth graph: no redirect guard, no session
// gating, no auth-state side effect. It exists only so the Login
// component is no longer unreachable. Do NOT add nav.login, do NOT
// add router-level auth checks, do NOT auto-redirect from this path,
// until the auth routing contract is intentionally designed.
// Tradier page (pages/Tradier.tsx) removed 2026-06-22 per ADR-029 P0.
// The dual-pane AccountPane view was superseded by Broker.tsx
// single-pane + runtime state machine. See ADR-029 §2.1.
import { R10Cockpit } from '../pages/R10Cockpit';
import { ErrorBoundary } from '../components/shared/ErrorBoundary';
import { useI18n } from '../i18n';
import { SystemResourcesBar } from '../components/system/SystemResourcesBar';
import { authClient } from '../api/authClient';

const Dashboard = React.lazy(() => import('../pages/Dashboard').then(module => ({ default: module.Dashboard })));
const RealtimeScoring = React.lazy(() => import('../pages/RealtimeScoring').then(module => ({ default: module.RealtimeScoring })));
const Broker = React.lazy(() => import('../pages/Broker').then(module => ({ default: module.Broker })));
const PaperAudit = React.lazy(() => import('../pages/PaperAudit').then(module => ({ default: module.PaperAudit })));
const R10Audit = React.lazy(() => import('../pages/R10Audit').then(module => ({ default: module.R10Audit })));
const Login = React.lazy(() => import('../pages/Login').then(module => ({ default: module.Login })));
const Admin = React.lazy(() => import('../pages/Admin').then(module => ({ default: module.Admin })));
const Settings = React.lazy(() => import('../pages/Settings').then(module => ({ default: module.Settings })));
const Databases = React.lazy(() => import('../pages/Databases').then(module => ({ default: module.Databases })));
const AutoTrading = React.lazy(() => import('../pages/AutoTrading').then(module => ({ default: module.AutoTrading })));

interface Route {
  path: string;
  label: string;
  component: React.ComponentType;
  requiresUIFrame?: boolean;
  showInNav?: boolean;
}

export const routes: Route[] = [
  // 2026-07-04 owner decision: R10 cockpit is the homepage; the V12
  // dashboard keeps living at /dashboard-v12. /cockpit stays routable
  // (bookmarks + backend auth whitelists) but leaves the nav bar.
  { path: '/', label: 'R10 驾驶舱', component: R10Cockpit },
  { path: '/dashboard-v12', label: 'Dashboard', component: Dashboard, requiresUIFrame: true },
  { path: '/cockpit', label: 'R10 驾驶舱', component: R10Cockpit, showInNav: false },
  { path: '/auto-trading', label: 'Auto Trading', component: AutoTrading },
  // { path: '/tradier', label: 'Tradier', component: Tradier }, // disabled — see Layer C gating note above
  { path: '/realtime-scoring', label: 'Realtime Scoring', component: RealtimeScoring },
  { path: '/broker', label: 'Broker', component: Broker },
  { path: '/audit', label: 'Audit', component: R10Audit },
  { path: '/paper-audit', label: 'Paper Audit', component: PaperAudit },
  { path: '/login', label: 'Login', component: Login, showInNav: false }, // orphan — see import block comment
  // Retired standalone pages: retain protected SPA aliases for old links.
  // Shared R10 backtests and audit APIs remain available to Auto Trading.
  { path: '/observability', label: 'Auto Trading', component: AutoTrading, showInNav: false },
  { path: '/replay', label: 'Auto Trading', component: AutoTrading, showInNav: false },
  { path: '/v14', label: 'Auto Trading', component: AutoTrading, showInNav: false },
  { path: '/admin', label: 'Admin', component: Admin },
  { path: '/settings', label: 'Settings', component: Settings },
  { path: '/databases', label: 'Databases', component: Databases },
];

export function routeRequiresUIFrame(pathname: string): boolean {
  let path = pathname || '/';
  try {
    path = new URL(pathname, window.location.origin).pathname || '/';
  } catch {
    // Keep the caller-provided pathname for non-URL test inputs.
  }
  return routes.find(r => r.path === path)?.requiresUIFrame === true;
}

export function Router() {
  const [path, setPath] = React.useState(window.location.pathname || '/');
  const { locale, setLocale, t } = useI18n();

  const route = routes.find(r => r.path === path) ?? routes[0];
  const Component = route.component;
  const showOperatorShell = route.path !== '/login';

  // '/' → 'dashboard', '/observability' → 'observability'
  const navKey = (p: string) => p === '/' ? 'dashboard' : p.slice(1);
  const navLabel = (r: Route) => (t.nav as Record<string, string | undefined>)[navKey(r.path)] ?? r.label;

  // ADR-025 P3 (2026-06-22): subscribe to v14-spa-navigate event so the
  // Router's `path` state mirrors window.location after SPA-internal
  // navigation (e.g. from useAuthGate's redirect to /login). This is
  // an additive effect — it does not add auth checks, preserving the
  // 6/21 ORPHAN policy. The click handler below still uses the same
  // setPath/pushState pair; this listener only catches programmatic
  // navigations performed by other modules.
  React.useEffect(() => {
    const onSpaNavigate = (e: Event) => {
      const detail = (e as CustomEvent<{ to: string }>).detail;
      const target = detail?.to ?? window.location.pathname;
      // Extract the pathname (drop search/hash) to keep the
      // routes.find() match contract consistent with the click
      // handler above (which sets path to r.path, no query string).
      try {
        const u = new URL(target, window.location.origin);
        setPath(u.pathname || '/');
      } catch {
        setPath(target);
      }
    };
    window.addEventListener('v14-spa-navigate', onSpaNavigate);
    return () => window.removeEventListener('v14-spa-navigate', onSpaNavigate);
  }, []);

  return (
    <ErrorBoundary>
      {showOperatorShell && <nav className="flex flex-col gap-1 whitespace-nowrap border-b border-gray-800 bg-gray-900 px-2 py-1 lg:flex-row lg:px-4">
        <div className="flex w-full min-w-0 gap-1 overflow-x-auto lg:flex-1">
          {routes.filter((r) => r.showInNav !== false).map((r) => (
            <button
              key={r.path}
              onClick={() => {
                setPath(r.path);
                window.history.pushState({}, '', r.path);
                window.dispatchEvent(new CustomEvent('v14-spa-navigate', { detail: { to: r.path } }));
              }}
              className={`shrink-0 px-3 py-1 text-sm rounded ${
                path === r.path
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {navLabel(r)}
            </button>
          ))}
        </div>
        <div className="flex w-full shrink-0 items-center gap-2 overflow-x-auto lg:ml-auto lg:w-auto">
          {/* CPU/MEM/LOAD/P bar — global, restored after the dist loss
              (the old bundle mounted it; src never had a mount point). */}
          <SystemResourcesBar />
          <button
            onClick={() => setLocale('en')}
            className={`px-2 py-1 text-xs rounded ${locale === 'en' ? 'bg-blue-700 text-white' : 'text-gray-500 hover:text-white'}`}
          >EN</button>
          <button
            onClick={() => setLocale('zh')}
            className={`px-2 py-1 text-xs rounded ${locale === 'zh' ? 'bg-blue-700 text-white' : 'text-gray-500 hover:text-white'}`}
          >中文</button>
          {/* Sign out. Confirms first: this bar sits above every page, so a
              stray click next to the language toggle would otherwise drop an
              operator out mid-session. A full page load (not SPA navigation)
              is deliberate — it discards all in-memory runtime/WS state rather
              than leaving a logged-out shell holding live subscriptions. */}
          <button
            onClick={async () => {
              if (!window.confirm(t.nav.logoutConfirm)) return;
              await authClient.logout();
              window.location.href = '/login';
            }}
            className="px-2 py-1 text-xs rounded text-gray-500 hover:text-white hover:bg-red-900/40"
            title={t.nav.logout}
          >{t.nav.logout}</button>
        </div>
      </nav>}
      <React.Suspense
        fallback={(
          <div className="flex min-h-[40vh] items-center justify-center bg-gray-950 text-sm text-gray-500">
            {locale === 'zh' ? '正在加载页面…' : 'Loading page…'}
          </div>
        )}
      >
        <Component />
      </React.Suspense>
    </ErrorBoundary>
  );
}
